import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

const badgeVariants = cva(
  // Type resolves through `--badge-*` sizing handles.
  'inline-flex items-center justify-center border text-[length:var(--badge-font-size)] font-[var(--badge-font-weight)] whitespace-nowrap shrink-0 gap-1 [&>svg]:size-3 transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-badge-default-bg text-badge-default-fg [a&]:hover:bg-badge-default-bg/90',
        secondary:
          'border-transparent bg-badge-secondary-bg text-badge-secondary-fg [a&]:hover:bg-badge-secondary-bg/90',
        destructive:
          'border-transparent bg-badge-destructive-bg text-badge-destructive-fg [a&]:hover:bg-badge-destructive-bg/90',
        success:
          'border-transparent bg-badge-success-bg text-badge-success-fg [a&]:hover:bg-badge-success-bg/90',
        warning:
          'border-transparent bg-badge-warning-bg text-badge-warning-fg [a&]:hover:bg-badge-warning-bg/90',
        outline:
          'border-badge-outline-border text-badge-outline-fg [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
      shape: {
        /* Label pill — the default chip. */
        pill: 'w-fit rounded-[var(--badge-radius)] px-[var(--badge-padding-x)] py-[var(--badge-padding-y)]',
        /* Count badge — a fixed-height circle that grows to a stadium for 2+
         * digits. Backs the green unread counter on chat rows. */
        count:
          'h-[var(--badge-count-size)] min-w-[var(--badge-count-size)] rounded-full px-[var(--badge-count-padding-x)] leading-none tabular-nums',
      },
    },
    defaultVariants: {
      variant: 'default',
      shape: 'pill',
    },
  },
);

function Badge({
  className,
  variant,
  shape,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, shape }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
